import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { PublicAccount } from '@his/shared';
import { publicPost, publicSession } from '@/lib/public-api';
import { describeError } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input } from '@/components/ui/primitives';

/**
 * Sign-in for the public directory: a phone number and a code.
 *
 * No password, because this is not staff — it is somebody booking a clinic
 * visit once, possibly from a borrowed phone.
 */
export function PublicSignInDialog({
  open,
  onClose,
  onSignedIn,
}: {
  open: boolean;
  onClose: () => void;
  onSignedIn: () => void;
}) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sent, setSent] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCode('');
    setSent(false);
    setDevCode(null);
    setError(null);
  };

  const request = useMutation({
    mutationFn: () => publicPost<{ sent: true; devCode?: string }>('/auth/otp', { phone }),
    onSuccess: (result) => {
      setSent(true);
      setDevCode(result.devCode ?? null);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const verify = useMutation({
    mutationFn: () =>
      publicPost<{ account: PublicAccount; accessToken: string }>('/auth/verify', {
        phone,
        code,
        name: name.trim() || undefined,
      }),
    onSuccess: (result) => {
      publicSession.setToken(result.accessToken);
      reset();
      onSignedIn();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Sign in"
      description="We send a six-digit code to your phone"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          {sent ? (
            <Button
              onClick={() => {
                setError(null);
                verify.mutate();
              }}
              loading={verify.isPending}
              disabled={code.trim().length !== 6}
            >
              Sign in
            </Button>
          ) : (
            <Button
              onClick={() => {
                setError(null);
                request.mutate();
              }}
              loading={request.isPending}
              disabled={phone.trim().length < 7}
            >
              Send code
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}

        <Field label="Phone number" required>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="03001234567"
            inputMode="tel"
            disabled={sent}
          />
        </Field>

        {sent && (
          <>
            {devCode && (
              <Alert>
                No SMS gateway is connected yet, so here is the code for testing:{' '}
                <span className="font-semibold tabular">{devCode}</span>
              </Alert>
            )}
            <Field label="Code" required>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                placeholder="123456"
                className="tabular"
              />
            </Field>
            <Field label="Your name" hint="Only used the first time, so hospitals know who is asking">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}
